<template>
  <!-- Row: Chart -->
  <div :id="'div' + divName" class="row f-row table_chart_height mt-1">
    <div class="row f-row" style="height:100%; width:100%;">
      <div class="col col-12 h-100">
        <div
          class="col col-12 text-center"
          v-if="!loadingAll && tableFormat.length === 0"
          style="width: 100%; top: calc(50% - 8px)!important; position: absolute;"
        >
          {{ noDataStr }}
        </div>
        <div
          v-else
          class="container__table-borderless"
          :style="{ 'padding-right': (isLarge ? 48 : 40) + 'px!important' }"
          style="padding-left: 1px"
        >
          <table
            class="table table-sm table-borderless curve_analysis_table"
            :class="isTotals && 'table__totals'"
          >
            <thead class="table-headers text-center align-center thead">
              <tr v-if="!isLarge">
                <th
                  :style="{
                    width: `${Math.round(
                      !isLarge ? divWidth / 12 : (4 * divWidth) / 12,
                    )}px !important`,
                  }"
                >
                  Date range
                </th>
                <td>
                  {{ dateValue }}
                </td>
              </tr>
              <tr
                v-for="(dat, idx) in tableFormat"
                v-bind:key="idx"
                :class="{
                  'div-prices': dat.divP,
                  'div-quantity': dat.divQ,
                }"
              >
                <template v-if="!dat.isDetails || showDetails">
                  <th
                    :class="{
                      ['text__prices' + divName]:
                        tableFormat.some((d) => d.divP) && idx === 0,
                      ['text__quantities' + divName]:
                        idx === tableFormat.length - 1,
                    }"
                    :style="widthTh()"
                  >
                    {{ shortenLabel(dat.labelTable) }}
                    <template
                      v-if="formatVars.hiddenLabel.includes(dat.labelTable)"
                    ></template>
                    <template v-else-if="dat.isPrices">
                      P ({{ `${dat.sUnit}/${dat.unit}` }})
                    </template>
                    <template v-else>
                      Q ({{ dat[isCurrency ? "sUnit" : "unit"] }})
                    </template>
                    <span
                      v-if="false"
                      style="visibility: hidden; width:0!important"
                    >
                      {{ showDetails }}
                    </span>
                  </th>
                  <td
                    v-for="(item, index) in zoomedData"
                    v-bind:key="index"
                    :class="{
                      tableHovered:
                        (formatVars['userHovered']['date'] === +item.date &&
                          formatVars['userHovered']['variable'] ===
                            dat['elementName'] &&
                          formatVars['userHovered']['hovered']) ||
                        (formatVars['userHovered']['fromChart'] &&
                          formatVars['userHovered']['date'] === +item.date &&
                          dat['elementName'].startsWith(
                            formatVars['userHovered']['variable'],
                          )),
                    }"
                    :style="{
                      'background-color': dat['colorBckTable'],
                      'border-bottom': '1px solid ' + dat['colorBdr'],
                    }"
                    @mouseover="onTableHover(dat, item, true, index)"
                    @mouseleave="onTableHover(dat, item, false, index)"
                  >
                    <span>
                      {{ formatItem(item, dat) }}
                    </span>
                    <span v-if="shouldShowPercentage(dat)">
                      ({{ formatPercentage(item, dat) }})
                    </span>
                  </td>
                </template>
              </tr>
            </thead>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { select } from "d3";
import { defaultAnalytics } from "@/_core/data/Analytics/AnalyticsHelpers.js";

export default {
  name: "CommodityTable",
  props: {
    Analytics: { required: true, default: defaultAnalytics() },
    colorTheme: { required: true, type: Boolean, default: true },
    divName: { required: false, default: "table" },
    formatVar: {
      default: {
        userHovered: {
          date: "",
          variable: "",
          hovered: false,
          fromChart: false,
        },
      },
    },
    isLarge: { required: false, type: Boolean, default: true },
    isTotals: { required: false, type: Boolean, default: false },
    loadingAll: { required: true, type: Boolean, default: false },
    noDataStr: { required: false, type: String },
    renderChartSignal: { required: true, default: 0 },
    showDetails: { required: true, type: Boolean, default: false },
    tableFormat: { required: true, type: Array, default: () => [] },
    zoomedData: { required: true, default: () => [] },
  },
  components: {},
  data() {
    return {
      divWidth: 100,
      regex: /\||%/,
      formatVars: {
        userHovered: {
          date: "",
          variable: "",
          hovered: false,
          fromChart: false,
        },
        hiddenLabel: [
          "Net - Mkt",
          "Hedge P&L",
          "Open Exposure",
          "Net - Budget",
        ],
      },
    };
  },
  computed: {
    dateValue() {
      return this.zoomedData[0] != null
        ? Object.keys(this.zoomedData[0]).indexOf("date") !== -1
          ? this.zoomedData[0]["date"]
          : "date"
        : "date";
    },
    isCurrency() {
      return this.Analytics.assetType === "currency";
    },
  },
  mounted() {
    this.calculateDivWidth();
  },
  watch: {
    renderChartSignal() {
      this.calculateDivWidth();
    },
    formatVar: {
      handler(newVal) {
        this.getChangeTableHover(newVal);
      },
      deep: true,
    },
  },
  methods: {
    shortenLabel(label) {
      return label === "Recognized"
        ? "Recog."
        : label === "Forecast"
        ? "Fcst"
        : label === "Market"
        ? "Mkt"
        : label;
    },
    calculateDivWidth() {
      let element = document.getElementById("div" + this.divName);
      if (element != null) {
        Object.assign(this, {
          divWidth: element?.clientWidth,
        });
        const { textP, textQ, topP, topQ, leftP, leftQ } = this.getTextValues(
          this.tableFormat.length,
        );

        this.generateTextStyle(
          textP,
          `text__prices${this.divName}`,
          topP,
          leftP,
        );
        this.generateTextStyle(
          textQ,
          `text__quantities${this.divName}`,
          topQ,
          leftQ,
        );
      }
    },
    formatItem(item, dat) {
      if (Number.isNaN(item[dat.nameData])) {
        return "-";
      } else if (item !== null) {
        if (this.getDynamicInfoPrices(dat.nameData)) {
          if (item[this.getDynamicInfoPrices(dat.nameData)] < 1000) {
            return item[this.getDynamicInfoPrices(dat.nameData)].toFixed(2);
          } else {
            return dat.formatF(item[this.getDynamicInfoPrices(dat.nameData)]);
          }
        } else {
          return dat.formatF(item[dat.nameData]);
        }
      }
      return "";
    },
    widthTh() {
      return {
        width: `${
          this.isLarge ? this.divWidth / 12 - 10 : (4 * this.divWidth) / 12
        }px !important`,
      };
    },
    getTextValues(tableFormatLength) {
      const textInfo = {
        short: {
          textP: "Px",
          textQ: "Qty",
          topP: 0,
          topQ: 25,
          leftP: 10,
          leftQ: 10,
        },
        long: {
          textP: "Prices",
          textQ: "Quantities",
          topP: 50,
          topQ: -70,
          leftP: 10,
          leftQ: 20,
        },
      };

      return tableFormatLength < 3 ? textInfo.short : textInfo.long;
    },
    generateTextStyle(content, selector, top, left) {
      const styleCSS = document.createElement("style");
      const divContainerTable = select(".curve_analysis_table").node();

      const divContainerTableWidth = divContainerTable
        ? divContainerTable.clientWidth
        : "";
      let divContainerTableTotalsWidth;
      if (this.isTotals) {
        const divContainerTableTotals = select(
          ".curve_analysis_table.table__totals",
        ).node();

        divContainerTableTotalsWidth = divContainerTableTotals
          ? divContainerTableTotals.clientWidth
          : "";
      }

      styleCSS.innerHTML = `
        .${selector} {
          position: relative;
        }
        th.${selector}::after {
          content: "${content}";
          position: absolute;
          transform: rotate(-90deg);
          top: ${top}%;
          transform-origin: center;
          white-space: nowrap;
          font-size: 12px;
          color: var(--white-gray);
          z-index: 1;
          text-shadow: 1px 1px 1px rgb(180 180 180 / 20%);
        }
        th#date-range::after {
          content: "${content}";
          position: absolute;
          transform: rotate(-90deg);
          top: ${top}%;
          left: ${this.divWidth / 12}px;
          transform-origin: center;
          white-space: nowrap;
          font-size: 12px;
          color: var(--white-gray);
          z-index: 1;
          text-shadow: 1px 1px 1px rgb(180 180 180 / 20%);
        }
        .curve_analysis_table thead tr th.${selector}::after {
          left: calc(${divContainerTableWidth}px - ${left}px) !important;
        }
        .curve_analysis_table.table__totals thead tr th.${selector}::after {
          left: calc(${divContainerTableTotalsWidth}px - ${left}px) !important;
        }
    `;

      document.head.appendChild(styleCSS);
    },
    onTableHover(dat, item, isHovered) {
      select(`.bar${dat["elementName"]}${+item.date}`).classed(
        "hoved",
        isHovered,
      );
      select(
        `.${dat["figureElements"]}${dat["elementName"]}${+item.date}`,
      ).classed("hoved", isHovered);

      select(`.${dat["elementName"]}${+item.date}`).classed("hoved", isHovered);

      Object.assign(this.formatVars, {
        userHovered: {
          date: +item.date,
          variable: dat["elementName"],
          hovered: isHovered,
          fromChart: false,
        },
      });
    },
    getPercFieldName(baseField, isPrices = true) {
      if (!isPrices) {
        return baseField.endsWith("Perc") ? baseField : `${baseField}QPerc`;
      }

      const {
        includeBasis,
        includeRatio,
        includeFutures,
      } = this.Analytics.userSelections;

      // Precios: determinar según las opciones del usuario
      if (includeFutures) {
        if (includeRatio) {
          if (includeBasis) {
            // futures + ratio + basis (precio completo)
            return `${baseField}PPerc`;
          } else {
            // futures + ratio (sin basis)
            return `${baseField}PFutRatioPerc`;
          }
        } else {
          if (includeBasis) {
            // futures + basis (sin ratio)
            return `${baseField}PFutPerc`;
          } else {
            // solo futures
            return `${baseField}PFutPerc`;
          }
        }
      } else {
        if (includeRatio) {
          if (includeBasis) {
            // ratio + basis (sin futures)
            return `${baseField}PRatioPerc`;
          } else {
            // solo ratio
            return `${baseField}PRatioPerc`;
          }
        } else {
          if (includeBasis) {
            // solo basis
            return `${baseField}POtherPerc`;
          }
        }
      }

      return `${baseField}PPerc`;
    },
    getDynamicInfoPrices(d) {
      let newNameData = "";
      let fldForecast = this.Analytics["userSelections"]["fldBudget"];
      let fldFixed = this.Analytics["userSelections"]["fldFixed"];
      let fldBasis = this.Analytics["userSelections"]["fldBasis"];
      let fldMkt = this.Analytics["userSelections"]["fldMkt"];
      let fldNet = this.Analytics["userSelections"]["fldNet"];

      if (fldForecast === undefined || fldForecast === null) {
        return;
      }
      if (d === "p_forecast" && fldForecast.includes("budgetP")) {
        newNameData = fldForecast;
      }
      if (fldBasis && d === "p_ratio") {
        newNameData = fldBasis;
      }
      if (d === "p_fixed" && fldFixed.includes("fixedP")) {
        newNameData = fldFixed;
      }
      if (d === "p_market" && fldMkt.includes("marketP")) {
        newNameData = fldMkt;
      }
      if (d === "p_net" && fldNet.includes("netP")) {
        newNameData = fldNet;
      }
      return newNameData;
    },
    getChangeTableHover(val) {
      Object.assign(this.formatVars, {
        userHovered: {
          date: val.date,
          variable: val.variable,
          hovered: false,
          fromChart: val.fromChart,
        },
      });
    },
    shouldShowPercentage(dat) {
      return (
        this.zoomedData.length < 16 &&
        dat.labelTable !== "Forecast" &&
        !this.formatVars.hiddenLabel.includes(dat.labelTable)
      );
    },
    formatPercentage(item, dat) {
      if (!item) return "-";

      const baseField = dat.unitPerc.replace(/(P|Q)Perc$/, "");
      const isPrices = dat.unitPerc.includes("PPerc");

      const dynamicField = this.getPercFieldName(baseField, isPrices);
      const percValue = item[dynamicField];

      if (this.regex.test(percValue)) {
        return percValue;
      }

      return dat.formatPerc ? dat.formatPerc(percValue) : percValue;
    },
  },
};
</script>

<style scoped></style>
